import React, { useState, useEffect, useCallback } from 'react';
import './AdminUsuarios.css';

const BASE_API = (process.env.REACT_APP_APIURL || 'http://localhost:4000').replace(/\/$/, '');

const AVATAR_COLORS = ['#c9960c','#a87c0a','#10b981','#d4a843','#ef4444','#b8860b','#6366f1'];
const colorFor = (name) => AVATAR_COLORS[(name || 'U').charCodeAt(0) % AVATAR_COLORS.length];

const fmt = (ts) =>
  ts ? new Date(ts).toLocaleString('es-ES', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }) : '—';

const KC_ROLES = [
  { name: 'admin',                label: 'Administrador' },
  { name: 'fase-practica',        label: 'Fase Práctica' },
  { name: 'finalizada-formacion', label: 'Formación Finalizada' },
];

const AdminUsuarios = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [expanded, setExpanded] = useState(null);
  const [kcData, setKcData]     = useState({});

  useEffect(() => {
    fetch(`${BASE_API}/admin/usuarios`)
      .then(r => r.json())
      .then(setUsuarios)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const loadKcRoles = useCallback((id) => {
    setKcData(prev => ({ ...prev, [id]: { roles: [], draft: [], loading: true, saving: false, error: null } }));
    fetch(`${BASE_API}/admin/usuarios/${id}/kc-roles`)
      .then(r => r.json())
      .then(roles => {
        if (!Array.isArray(roles)) throw new Error(roles.error || 'Error cargando roles');
        setKcData(prev => ({ ...prev, [id]: { roles, draft: [...roles], loading: false, saving: false, error: null } }));
      })
      .catch(err => setKcData(prev => ({ ...prev, [id]: { roles: [], draft: [], loading: false, saving: false, error: err.message } })));
  }, []);

  const toggle = (id) => {
    const opening = expanded !== id;
    setExpanded(opening ? id : null);
    if (opening && !kcData[id]) loadKcRoles(id);
  };

  const toggleRole = (userId, role) => {
    setKcData(prev => {
      const entry = prev[userId] || { draft: [] };
      const draft = entry.draft.includes(role)
        ? entry.draft.filter(r => r !== role)
        : [...entry.draft, role];
      return { ...prev, [userId]: { ...entry, draft } };
    });
  };

  const saveRoles = async (userId) => {
    const data = kcData[userId];
    if (!data) return;
    setKcData(prev => ({ ...prev, [userId]: { ...prev[userId], saving: true, error: null } }));
    try {
      const resp = await fetch(`${BASE_API}/admin/usuarios/${userId}/kc-roles`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roles: data.draft }),
      });
      if (!resp.ok) throw new Error((await resp.json()).error);
      setKcData(prev => ({ ...prev, [userId]: { ...prev[userId], roles: [...data.draft], saving: false } }));
    } catch (err) {
      setKcData(prev => ({ ...prev, [userId]: { ...prev[userId], saving: false, error: err.message } }));
    }
  };

  const filtered = usuarios.filter(u => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      u.username?.toLowerCase().includes(q)  ||
      u.email?.toLowerCase().includes(q)     ||
      u.first_name?.toLowerCase().includes(q)||
      u.last_name?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="au-page">
      <div className="au-header">
        <div>
          <h1 className="au-title">Control de Usuarios</h1>
          <p className="au-subtitle">{usuarios.length} usuarios registrados</p>
        </div>
      </div>

      <div className="au-toolbar">
        <input
          className="au-search"
          placeholder="Buscar por nombre, usuario o email…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="au-loading"><span className="au-spinner" />Cargando usuarios…</div>
      ) : (
        <div className="au-table-wrap">
          <table className="au-table">
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Email</th>
                <th>Rol</th>
                <th>Módulos</th>
                <th>IP Primer Acceso</th>
                <th>IP Último Acceso</th>
                <th>Último Acceso</th>
                <th>Registrado</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(u => {
                const isOpen   = expanded === u.id;
                const initials = ((u.first_name || u.username || 'U').slice(0, 1) +
                                  (u.last_name  || '').slice(0, 1)).toUpperCase() || 'U';
                const pct = u.total_modulos > 0
                  ? Math.round((u.modulos_completados / u.total_modulos) * 100) : 0;
                const kc = kcData[u.id];

                return (
                  <React.Fragment key={u.id}>
                    <tr
                      className={`au-row ${isOpen ? 'au-row--open' : ''}`}
                      onClick={() => toggle(u.id)}
                    >
                      <td>
                        <div className="au-user-cell">
                          <div className="au-avatar" style={{ background: colorFor(u.username) }}>
                            {initials}
                          </div>
                          <div className="au-user-info">
                            <span className="au-name">
                              {u.first_name || ''} {u.last_name || ''}
                              {(!u.first_name && !u.last_name) && u.username}
                            </span>
                            <span className="au-username">@{u.username}</span>
                            {u.user_tag && <span className="au-badge">{u.user_tag}</span>}
                          </div>
                        </div>
                      </td>
                      <td className="au-email">{u.email || '—'}</td>
                      <td>
                        <span className={`au-role au-role--${u.role}`}>{u.role}</span>
                      </td>
                      <td>
                        <div className="au-progress">
                          <span className="au-progress-label">{u.modulos_completados}/{u.total_modulos}</span>
                          <div className="au-progress-bar">
                            <div className="au-progress-fill" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      </td>
                      <td className="au-ip">{u.first_login_ip || '—'}</td>
                      <td className="au-ip">{u.last_login_ip  || '—'}</td>
                      <td className="au-date">{fmt(u.last_login_at)}</td>
                      <td className="au-date">{fmt(u.created_at)}</td>
                    </tr>

                    {isOpen && (
                      <tr className="au-detail-row">
                        <td colSpan={8}>
                          <div className="au-detail">

                            {/* Módulos completados */}
                            <div className="au-detail-section">
                              <h4 className="au-detail-title">
                                Módulos completados ({u.modulos_completados} de {u.total_modulos})
                              </h4>
                              {u.modulos && u.modulos.length > 0 ? (
                                <ul className="au-modulos-list">
                                  {u.modulos.map(m => (
                                    <li key={m.id} className="au-modulo-item">
                                      <span className="au-modulo-num">Módulo {m.modulo_name}</span>
                                      <span className="au-modulo-desc">{m.modulo_description}</span>
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <p className="au-empty-msg">Ningún módulo completado todavía.</p>
                              )}
                            </div>

                            {/* Datos de acceso */}
                            <div className="au-detail-section">
                              <h4 className="au-detail-title">Datos de acceso</h4>
                              <div className="au-detail-grid">
                                <span className="au-detail-label">ID Keycloak</span>
                                <code className="au-detail-code">{u.id}</code>
                                <span className="au-detail-label">IP primer acceso</span>
                                <code className="au-detail-code">{u.first_login_ip || '—'}</code>
                                <span className="au-detail-label">IP último acceso</span>
                                <code className="au-detail-code">{u.last_login_ip || '—'}</code>
                                <span className="au-detail-label">Último acceso</span>
                                <span className="au-detail-val">{fmt(u.last_login_at)}</span>
                                <span className="au-detail-label">Fecha registro</span>
                                <span className="au-detail-val">{fmt(u.created_at)}</span>
                              </div>
                            </div>

                            {/* Roles Keycloak */}
                            <div className="au-detail-section au-roles-section" onClick={e => e.stopPropagation()}>
                              <h4 className="au-detail-title">Roles Keycloak</h4>
                              {!kc || kc.loading ? (
                                <span className="au-spinner" />
                              ) : (
                                <>
                                  <div className="au-roles-list">
                                    {KC_ROLES.map(({ name, label }) => (
                                      <label key={name} className="au-role-check">
                                        <input
                                          type="checkbox"
                                          checked={kc.draft.includes(name)}
                                          onChange={() => toggleRole(u.id, name)}
                                        />
                                        <span className={`au-role-tag au-role-tag--${name}`}>{label}</span>
                                      </label>
                                    ))}
                                  </div>
                                  {kc.error && <p className="au-roles-error">{kc.error}</p>}
                                  <button
                                    className="au-roles-save"
                                    onClick={() => saveRoles(u.id)}
                                    disabled={kc.saving}
                                  >
                                    {kc.saving ? 'Guardando…' : 'Guardar roles'}
                                  </button>
                                </>
                              )}
                            </div>

                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 && !loading && (
            <div className="au-empty">No hay usuarios que coincidan con la búsqueda.</div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminUsuarios;
